import { useMemo } from 'react';
import { useDrillsData } from './domain/useDrillsData';
import { useDrillsConfig } from './domain/drills/useDrillsConfig';
import { useEquipmentRegister } from './domain/useEquipmentRegister';
import { useCrew } from './useCrew';
import { useDefects } from './domain/defects/useDefects';
import { useMaintenanceSchedule } from './domain/useMaintenanceSchedule';
// FT-116 REWRITE: Use fleet RAs instead of vessel-scoped
import { useFleetRiskAssessments } from './domain/fleet/useFleetRiskAssessments';
// FT-153: Add Compliance Register for SMS document expiry dates
import { useComplianceRegister } from './domain/useComplianceRegister';
import { useVessel } from '../contexts/VesselContext';
import { Flame, Wrench, Users, AlertCircle, ShieldAlert, GraduationCap, FileCheck } from 'lucide-react';
import { getJobDisplayName } from '../utils/maintenanceJobHelpers';
// FT-189: Centralized drill name resolution
import { getDrillRecordDisplayName } from '../components/tailwind/modules/drills/utils/drillsHelpers';

/**
 * FT-153: Convert Date to local YYYY-MM-DD string (avoids UTC timezone bug)
 * Previous: date.toISOString().split('T')[0] caused off-by-one errors in UK timezone
 * @param {Date} date - The date to convert
 * @returns {string} YYYY-MM-DD format in local timezone
 */
const toLocalDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Calculate priority level for a given date's events
 * @param {Array} events - Events on that date
 * @returns {Object} { priority: 'red'|'amber'|'green', overdueCount, warningCount }
 */
const calculateDatePriority = (events) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const warningThreshold = new Date(today);
  warningThreshold.setDate(warningThreshold.getDate() + 3);

  let overdueCount = 0;
  let warningCount = 0;

  events.forEach(event => {
    if (event.isOverdue) {
      overdueCount++;
    } else if (event.date <= warningThreshold) {
      warningCount++;
    }
  });

  let priority = 'green';
  if (overdueCount > 0) {
    priority = 'red';
  } else if (warningCount > 0) {
    priority = 'amber';
  }

  return { priority, overdueCount, warningCount };
};

/**
 * Aggregates all upcoming events from all modules
 * Returns events grouped by date for calendar display
 *
 * FT-153: Fixed timezone bug (toISOString → toLocalDateString)
 * FT-153: Fixed crew cert field name (added expiry_date)
 * FT-153: Added Compliance Register integration
 *
 * @returns {Object} { events, eventsByDate, getEventsForDate, getEventsForWeek, getIconCountsForDate }
 */
export const useUpcomingEvents = () => {
  const drillsData = useDrillsData();
  // FT-471: Use user-curated drill list (WBC3 mandatory + company required) instead of
  // auto-populated drill_register which contained ghost entries (GMDSS DSC etc.).
  const { drillList } = useDrillsConfig();
  const { register: equipmentRegister } = useEquipmentRegister();
  const { crew } = useCrew();
  const defectsData = useDefects();
  const { schedule: maintenanceSchedule } = useMaintenanceSchedule();
  // FT-116 REWRITE: Use fleet RAs instead of vessel-scoped
  const { vesselRiskAssessments } = useFleetRiskAssessments();
  // FT-153: Add Compliance Register
  const { items: complianceItems } = useComplianceRegister();
  const { currentVesselId } = useVessel();

  const events = useMemo(() => {
    const allEvents = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // ========================================
    // DRILLS
    // FT-471: Reads from drillList (useDrillsConfig) — the user-curated list
    // (WBC3 mandatory: Fire, Abandon Ship, MOB + company-required drills).
    // Previously read from drill_register_<vesselId> via getDrillRegister() which
    // was auto-populated with ghost entries (GMDSS DSC, duplicate drills etc.).
    // Next-due dates are derived from actual completion records in drills_<vesselId>.
    // ========================================
    try {
      // FT-458: use the tombstone-filtered view so a soft-deleted drill can't
      // surface as an upcoming/most-recent event.
      const allDrills = drillsData?.visibleDrills || drillsData?.drills || [];

      (drillList || []).forEach(drill => {
        const drillType = drill.type || drill.drillType;
        let nextDueDate = null;

        // Derive next due from last completion + frequency (mirrors useDashboardCategories)
        const completedDrills = allDrills
          .filter(d => d.type === drillType && d.date)
          .sort((a, b) => new Date(b.date) - new Date(a.date));

        if (completedDrills.length > 0) {
          const lastCompletedDate = new Date(completedDrills[0].date);
          const frequencyMs = (drill.frequencyDays || 30) * 24 * 60 * 60 * 1000;
          nextDueDate = new Date(lastCompletedDate.getTime() + frequencyMs);
        } else {
          // Never completed — show as overdue (due today)
          nextDueDate = today;
        }

        if (nextDueDate && !isNaN(nextDueDate.getTime())) {
          const isOverdue = nextDueDate < today;
          const daysOverdue = isOverdue
            ? Math.floor((today - nextDueDate) / (1000 * 60 * 60 * 24))
            : 0;

          allEvents.push({
            id: `drill-${drillType}`,
            type: 'drill',
            icon: Flame,
            iconColor: 'text-cyan-600',
            title: drill.name || drillType,
            date: nextDueDate,
            dateString: toLocalDateString(nextDueDate), // FT-153: Fixed timezone bug
            isOverdue,
            daysOverdue,
            module: 'drills'
          });
        }
      });
    } catch (error) {
      console.error('[useUpcomingEvents] Drills error:', error);
    }

    // ========================================
    // MAINTENANCE (Uses domain hook - no direct localStorage access)
    // ========================================
    try {
      if (maintenanceSchedule && maintenanceSchedule.length > 0) {
        maintenanceSchedule.forEach(job => {
          let nextDueDate = null;

          // PRIORITY 1: Use job.nextDue if available (this is where PlannedMaintenanceTab saves it)
          if (job.nextDue) {
            nextDueDate = new Date(job.nextDue);
          }
          // PRIORITY 2: Calculate from lastCompleted + interval
          else if (job.lastCompleted && job.interval && job.unit) {
            const lastDate = new Date(job.lastCompleted);
            nextDueDate = calculateNextDue(lastDate, job.interval, job.unit);
          }
          // PRIORITY 3: Calculate from today if never completed
          else if (job.interval && job.unit) {
            nextDueDate = calculateNextDue(new Date(), job.interval, job.unit);
          }

          const hasDate = nextDueDate && !isNaN(nextDueDate.getTime());
          if (hasDate) {
            const isOverdue = nextDueDate < today;
            const daysOverdue = isOverdue
              ? Math.floor((today - nextDueDate) / (1000 * 60 * 60 * 24))
              : 0;

            allEvents.push({
              id: `maintenance-${job.id}`,
              type: 'equipment',
              icon: Wrench,
              iconColor: 'text-cyan-600',
              title: getJobDisplayName(job),
              date: nextDueDate,
              dateString: toLocalDateString(nextDueDate), // FT-153: Fixed timezone bug
              isOverdue,
              daysOverdue,
              module: 'maintenance'
            });
          }
        });
      }
    } catch (error) {
      console.error('[useUpcomingEvents] Maintenance error:', error);
    }

    // ========================================
    // CREW CERTIFICATES (Remove 90-day filter to match category cards)
    // FT-153: Fixed field name - expiry_date is the actual field (snake_case)
    // ========================================
    try {
      if (crew && Array.isArray(crew)) {
        crew.forEach(crewMember => {
          // Skip inactive crew
          const status = (crewMember.status || 'active').toLowerCase();
          if (status !== 'active') return;

          if (crewMember.certificates && Array.isArray(crewMember.certificates)) {
            crewMember.certificates.forEach(cert => {
              // FT-153: Fixed - expiry_date is the actual field (snake_case, verified in FT-148)
              const expiryDateStr = cert.expiry_date || cert.expiryDate || cert.expiry;
              if (expiryDateStr) {
                const expiryDate = new Date(expiryDateStr);
                if (!isNaN(expiryDate.getTime())) {
                  const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
                  const isOverdue = expiryDate < today;

                  // Show ALL certificates (removed 90-day filter to match category cards)
                  allEvents.push({
                    id: `cert-${crewMember.id}-${cert.id || cert.name}`,
                    type: 'certificate',
                    icon: Users,
                    iconColor: 'text-cyan-600',
                    title: `${crewMember.name} - ${cert.name || cert.certificateType || 'Certificate'}`,
                    date: expiryDate,
                    dateString: toLocalDateString(expiryDate), // FT-153: Fixed timezone bug
                    isOverdue,
                    daysOverdue: isOverdue ? Math.abs(daysUntilExpiry) : 0,
                    module: 'crew'
                  });
                }
              }
            });
          }
        });
      }
    } catch (error) {
      console.error('[useUpcomingEvents] Crew error:', error);
    }

    // ========================================
    // DEFECTS (Keep existing logic - critical defects with deadlines)
    // ========================================
    try {
      const defectsList = defectsData?.defects || [];
      if (Array.isArray(defectsList)) {
        defectsList.forEach(defect => {
          const status = (defect.status || '').toLowerCase();
          if (status === 'resolved' || status === 'closed') return;

          const priority = (defect.priority || '').toLowerCase();
          const severity = (defect.severity || '').toLowerCase();
          const isCritical = priority === 'critical' || priority === 'high' ||
                            severity === 'critical' || severity === 'high';

          // Only show critical defects with deadlines
          if (isCritical && defect.deadline) {
            const deadline = new Date(defect.deadline);
            if (!isNaN(deadline.getTime())) {
              const daysOverdue = deadline < today
                ? Math.floor((today - deadline) / (1000 * 60 * 60 * 24))
                : 0;

              allEvents.push({
                id: `defect-${defect.id}`,
                type: 'defect',
                icon: AlertCircle,
                iconColor: 'text-cyan-600',
                title: defect.title || defect.description || 'Critical Defect',
                date: deadline,
                dateString: toLocalDateString(deadline), // FT-153: Fixed timezone bug
                isOverdue: deadline < today,
                daysOverdue,
                module: 'defects'
              });
            }
          }
        });
      }
    } catch (error) {
      console.error('[useUpcomingEvents] Defects error:', error);
    }

    // ========================================
    // RISK ASSESSMENTS (nextReviewDate - annual review requirement)
    // ========================================
    try {
      // FT-116 REWRITE: Use fleet RAs (pre-filtered for current vessel)
      const riskAssessments = vesselRiskAssessments || [];

      riskAssessments.forEach(assessment => {
        // Only show active assessments with review dates
        if (assessment.status === 'active' && assessment.nextReviewDate) {
          const reviewDate = new Date(assessment.nextReviewDate);

          if (!isNaN(reviewDate.getTime())) {
            const isOverdue = reviewDate < today;
            const daysOverdue = isOverdue
              ? Math.floor((today - reviewDate) / (1000 * 60 * 60 * 24))
              : 0;

            allEvents.push({
              id: `risk-${assessment.id}`,
              type: 'risk_assessment',
              icon: ShieldAlert,
              iconColor: 'text-cyan-600',
              title: assessment.title || 'Risk Assessment Review',
              date: reviewDate,
              dateString: toLocalDateString(reviewDate), // FT-153: Fixed timezone bug
              isOverdue,
              daysOverdue,
              module: 'risk'
            });
          }
        }
      });
    } catch (error) {
      console.error('[useUpcomingEvents] Risk assessments error:', error);
    }

    // ========================================
    // FAMILIARISATION (7 days from join date, WBC3 Section 14.11.3)
    // ========================================
    try {
      const crewMembers = crew || [];

      crewMembers.forEach(member => {
        // Skip inactive crew
        const status = (member.status || 'active').toLowerCase();
        if (status !== 'active') return;

        // Only show if familiarisation not complete and they have a join date
        if (member.joinDate && !member.familiarisationComplete && !member.familiarisation && !member.familiarisationDate && !member.familiarisationCompleted) {
          const joinDate = new Date(member.joinDate);

          // Familiarisation due 7 days after joining
          const famDeadline = new Date(joinDate);
          famDeadline.setDate(famDeadline.getDate() + 7);

          if (!isNaN(famDeadline.getTime())) {
            const isOverdue = famDeadline < today;
            const daysOverdue = isOverdue
              ? Math.floor((today - famDeadline) / (1000 * 60 * 60 * 24))
              : 0;

            const memberName = member.name ||
              `${member.firstName || ''} ${member.lastName || ''}`.trim() ||
              'Crew Member';

            allEvents.push({
              id: `fam-${member.id}`,
              type: 'familiarisation',
              icon: GraduationCap,
              iconColor: 'text-cyan-600',
              title: `${memberName} - Familiarisation`,
              date: famDeadline,
              dateString: toLocalDateString(famDeadline), // FT-153: Fixed timezone bug
              isOverdue,
              daysOverdue,
              module: 'crew'
            });
          }
        }
      });
    } catch (error) {
      console.error('[useUpcomingEvents] Familiarisation error:', error);
    }

    // ========================================
    // FT-153: COMPLIANCE REGISTER (SMS documents, certificates, risk assessments with expiry)
    // ========================================
    try {
      const complianceList = complianceItems || [];

      complianceList.forEach(item => {
        // Only show items with expiry dates
        if (item.expiryDate) {
          const expiryDate = new Date(item.expiryDate);

          if (!isNaN(expiryDate.getTime())) {
            const isOverdue = expiryDate < today;
            const daysOverdue = isOverdue
              ? Math.floor((today - expiryDate) / (1000 * 60 * 60 * 24))
              : 0;

            allEvents.push({
              id: `compliance-${item.id}`,
              type: 'compliance',
              icon: FileCheck,
              iconColor: 'text-cyan-600',
              title: item.name || item.title || 'Compliance Item',
              date: expiryDate,
              dateString: toLocalDateString(expiryDate),
              isOverdue,
              daysOverdue,
              module: 'compliance',
              category: item.category // certificate, sms_document, risk_assessment
            });
          }
        }
      });
    } catch (error) {
      console.error('[useUpcomingEvents] Compliance Register error:', error);
    }

    // Sort by date (earliest first)
    allEvents.sort((a, b) => a.date - b.date);

    return allEvents;
  // FT-471: added drillList to deps (replaces getDrillRegister() from drill_register_<vesselId>)
  }, [drillsData, drillList, crew, defectsData, maintenanceSchedule, vesselRiskAssessments, complianceItems, currentVesselId]);

  // Group events by date (YYYY-MM-DD)
  const eventsByDate = useMemo(() => {
    const grouped = {};
    events.forEach(event => {
      if (!grouped[event.dateString]) {
        grouped[event.dateString] = [];
      }
      grouped[event.dateString].push(event);
    });
    return grouped;
  }, [events]);

  // Helper: Get events for specific date
  // FT-153: Fixed - use toLocalDateString instead of toISOString
  const getEventsForDate = (date) => {
    const dateString = toLocalDateString(date);
    return eventsByDate[dateString] || [];
  };

  // Helper: Get events for specific week
  // FT-153: Fixed - use toLocalDateString instead of toISOString
  const getEventsForWeek = (weekStartDate) => {
    const weekEvents = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStartDate);
      day.setDate(day.getDate() + i);
      const dayEvents = getEventsForDate(day);
      if (dayEvents.length > 0) {
        weekEvents.push({
          date: day,
          dateString: toLocalDateString(day),
          events: dayEvents
        });
      }
    }
    return weekEvents;
  };

  // Helper: Get icon counts for a date (for calendar display)
  const getIconCountsForDate = (date) => {
    const dateEvents = getEventsForDate(date);
    const counts = {
      drill: 0,
      equipment: 0,
      certificate: 0,
      defect: 0,
      risk_assessment: 0,
      familiarisation: 0,
      compliance: 0, // FT-153: Added compliance type
      total: dateEvents.length,
      hasOverdue: false,
      // Priority fields for calendar color coding
      priority: 'green',
      overdueCount: 0,
      warningCount: 0
    };

    dateEvents.forEach(event => {
      if (counts.hasOwnProperty(event.type)) {
        counts[event.type]++;
      }
      if (event.isOverdue) counts.hasOverdue = true;
    });

    // Calculate priority (only if there are events)
    if (dateEvents.length > 0) {
      const priorityInfo = calculateDatePriority(dateEvents);
      counts.priority = priorityInfo.priority;
      counts.overdueCount = priorityInfo.overdueCount;
      counts.warningCount = priorityInfo.warningCount;
    }

    return counts;
  };

  return {
    events,
    eventsByDate,
    getEventsForDate,
    getEventsForWeek,
    getIconCountsForDate
  };
};

/**
 * Calculate next due date based on interval and unit
 * (Same helper as useDashboardCategories)
 */
function calculateNextDue(fromDate, interval, unit) {
  if (!interval || !unit) return null;

  const date = new Date(fromDate);
  const normalizedUnit = unit.toLowerCase().replace(/s$/, '');

  switch (normalizedUnit) {
    case 'day':
      date.setDate(date.getDate() + interval);
      break;
    case 'week':
      date.setDate(date.getDate() + (interval * 7));
      break;
    case 'month':
      date.setMonth(date.getMonth() + interval);
      break;
    case 'year':
      date.setFullYear(date.getFullYear() + interval);
      break;
    default:
      return null;
  }

  return date;
}
